// The enemy camps a cleared stage opens onto.
//
// Each entry is a piece of *territory*: the ground it adds to the board, and
// the army standing on that ground. Ground and army are one table rather than
// two because they have to agree — a formation whose pieces land in a hole is
// a piece that cannot move and a king that cannot be mated, which would stall
// the roguelike loop rather than merely look wrong.
//
// Coordinates are relative to an anchor and are laid out as if the camp were
// being approached from the south (the player advancing up the board). They
// are rotated into place by `orient`, so one entry serves all four directions
// and the table stays readable.
//
// The board does not have to stay a rectangle. These shapes are deliberately
// ragged — a lobe, a pair of wings, a spur off one corner — because a board
// that grows by tacking on rectangles reads as a spreadsheet, and the whole
// point of opening it outward is that the map becomes a place.

import {
  BISHOP,
  KING,
  KNIGHT,
  makePiece,
  PAWN,
  type PieceType,
  QUEEN,
  ROOK,
  type Side,
  square
} from "src/engine/rules/board";

import { AUTHORED_CAMPS } from "./authoredCamps";

/** A square of the camp, relative to its anchor. */
export type Cell = readonly [file: number, rank: number];
/** A piece standing on one of those squares. */
export type Placement = readonly [file: number, rank: number, type: PieceType];

export interface Formation {
  id: string;
  name: string;
  /** Roughly how hard it is, so a stage can reach for something its size. */
  weight: number;
  /**
   * Earliest stage this may appear on.
   *
   * Weight alone is not enough. A fortress and a skirmish camp can cost the
   * same on paper and be completely different things to walk into on your
   * second stage, and a camp the player cannot yet answer is not a difficulty
   * curve — it is a wall with a stage counter behind it.
   */
  minStage?: number;
  /**
   * The army this is worth putting in front of.
   *
   * A citadel against a lone king is not a fight, it is a dead end: nothing
   * about it is winnable and the run simply stops. And a three-pawn outpost
   * against a full army is a formality that costs a turn. Both ends matter,
   * so both are expressible.
   */
  minPlayerPieces?: number;
  maxPlayerPieces?: number;
  ground: readonly Cell[];
  pieces: readonly Placement[];
  /**
   * The camp's own industry, in the same relative coordinates as its ground.
   *
   * **Empty on every hand-written camp in this table, and that is the point.**
   * A procedural camp is pieces on a shape; an *authored* one is a room
   * somebody drew, and a room may have a seam of ore under it, refined metal
   * on the floor, pawns turned the way the author aimed them and buildings
   * standing on it. `formationFromRoom` is what fills these, and everything
   * downstream — `orient`, `placeFormation`, the wave, `next-stage` — treats
   * them exactly as it treats the ground.
   *
   * A **facing turns with the camp**: these are laid out as if approached from
   * the south and rotated into place, so a direction that did not rotate would
   * face the player from one of the four approaches and away on the other
   * three. That is a rotation on the direction index, which is why the two
   * cannot be separate passes.
   *
   * A structure carries its **kind and not its owner**, exactly as a placement
   * carries its rank and not its side: the camp's side is decided when it is
   * put on the board, and a room that hardcoded one would arrive garrisoned by
   * whoever happened to be side 1 in the level it was drawn for.
   */
  heaps?: readonly (readonly [file: number, rank: number, amount: number])[];
  metal?: readonly (readonly [file: number, rank: number, grade: number, count: number])[];
  facing?: readonly (readonly [file: number, rank: number, direction: number])[];
  structures?: readonly (readonly [file: number, rank: number, kind: number])[];
}

/** What the board looks like when a camp is being chosen. */
export interface Muster {
  /** How far into the run this is. */
  stage: number;
  /** How much the player has left to fight with, kings included. */
  playerPieces: number;
  /** The heaviest formation this stage can afford. */
  tier: number;
}

/** A solid block, for the shapes that want one under them. */
function block(fromFile: number, toFile: number, fromRank: number, toRank: number): Cell[] {
  const out: Cell[] = [];
  for (let rank = fromRank; rank <= toRank; rank++) {
    for (let file = fromFile; file <= toFile; file++) out.push([file, rank]);
  }
  return out;
}

const HAND_WRITTEN: readonly Formation[] = [
  {
    id: "outpost",
    name: "Outpost",
    weight: 1,
    // Stops appearing once you have an army. Five pieces is a fight on stage
    // one and a formality on stage six, and a formality that costs a turn is
    // worse than nothing at all.
    maxPlayerPieces: 10,
    // A small hook of ground: three squares deep, with one arm reaching west.
    ground: [...block(0, 3, 0, 2), [-1, 1], [-1, 2], [-2, 2]],
    pieces: [
      [1, 2, KING],
      [0, 1, PAWN],
      [1, 1, PAWN],
      [2, 1, KNIGHT],
      // A bishop rather than a third pawn. Even the opening camp should have
      // something in it that has to be answered rather than walked around.
      [-1, 2, BISHOP]
    ]
  },
  {
    id: "wings",
    name: "Twin wings",
    weight: 2,
    // Two lobes either side of a narrow neck — a shape with a genuine hole in
    // the middle of its bounding box.
    ground: [
      ...block(-3, -1, 1, 3),
      ...block(2, 4, 1, 3),
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1]
    ],
    pieces: [
      [0, 1, KING],
      [-2, 2, ROOK],
      [3, 2, KNIGHT],
      [-3, 1, PAWN],
      [-1, 1, PAWN],
      [2, 1, PAWN],
      [4, 1, PAWN],
      [-2, 3, BISHOP]
    ]
  },
  {
    id: "redoubt",
    name: "Redoubt",
    weight: 3,
    // A wide front with a spur off the eastern corner.
    ground: [...block(-2, 4, 0, 3), [5, 2], [5, 3], [6, 3]],
    pieces: [
      [1, 3, KING],
      [0, 3, QUEEN],
      [-2, 3, ROOK],
      [4, 3, ROOK],
      [6, 3, BISHOP],
      [-1, 1, PAWN],
      [0, 1, PAWN],
      [1, 1, PAWN],
      [2, 1, PAWN],
      [3, 1, PAWN],
      [5, 2, KNIGHT]
    ]
  },
  {
    id: "atoll",
    name: "The Atoll",
    weight: 4,
    // A ring with an actual hole in it — the board is a set of squares, and
    // this is the entry that proves it.
    ground: [
      ...block(-2, 4, 0, 0),
      ...block(-2, 4, 4, 4),
      [-2, 1],
      [-2, 2],
      [-2, 3],
      [4, 1],
      [4, 2],
      [4, 3],
      // A causeway to the middle.
      //
      // The centre square used to be an island: ground with nothing touching
      // it, so the king standing there could not be walked to and the stage it
      // belonged to could never be cleared. The ring and its holes are the
      // point of this camp; a marooned king was not.
      [1, 1],
      [1, 2],
      [1, 3]
    ],
    pieces: [
      [1, 2, KING],
      [-2, 2, ROOK],
      [4, 2, ROOK],
      [0, 0, PAWN],
      [2, 0, PAWN],
      [0, 4, KNIGHT],
      [2, 4, KNIGHT],
      [-2, 0, BISHOP],
      [4, 4, QUEEN]
    ]
  },
  {
    id: "gauntlet",
    name: "The Gauntlet",
    weight: 5,
    minStage: 3,
    // A corridor you have to run, with alcoves that cover it. Three wide and
    // ten deep: the shape *is* the difficulty, because there is no way round
    // and every square of it is seen from somewhere.
    ground: [...block(0, 2, 0, 9), [-1, 2], [-1, 3], [3, 6], [3, 7]],
    pieces: [
      [1, 9, KING],
      [0, 9, ROOK],
      [2, 9, ROOK],
      // The alcoves, each holding a piece that rakes the corridor lengthways.
      [-1, 3, BISHOP],
      [3, 6, BISHOP],
      [1, 6, KNIGHT],
      [1, 3, KNIGHT],
      [0, 1, PAWN],
      [1, 1, PAWN],
      [2, 1, PAWN]
    ]
  },
  {
    id: "crossroads",
    name: "The Crossroads",
    weight: 5,
    minStage: 3,
    // Four arms and a middle. Knights at the tips, because a knight is the
    // piece that does not care that the arms are one square wide.
    // The vertical bar is split around the horizontal one so the two do not
    // name the same squares twice. Harmless downstream, but a table that
    // repeats itself is a table nobody can read a size off.
    ground: [...block(-4, 4, 2, 4), ...block(-1, 1, 0, 1), ...block(-1, 1, 5, 6)],
    pieces: [
      [0, 4, KING],
      [-4, 3, KNIGHT],
      [4, 3, KNIGHT],
      [0, 6, KNIGHT],
      [0, 0, KNIGHT],
      [-2, 4, BISHOP],
      [2, 4, BISHOP],
      [0, 2, ROOK],
      [-1, 3, PAWN],
      [1, 3, PAWN]
    ]
  },
  {
    id: "shattered",
    name: "The Shattered Line",
    weight: 6,
    minStage: 4,
    minPlayerPieces: 6,
    // Three islands on one bridge each. You cannot fight it all at once, and
    // whichever island you take first is a real decision — which is the thing
    // a wide open camp never asks.
    ground: [
      ...block(-6, -4, 0, 2),
      ...block(-1, 1, 0, 2),
      ...block(4, 6, 0, 2),
      [-3, 1],
      [-2, 1],
      [2, 1],
      [3, 1]
    ],
    pieces: [
      [0, 1, KING],
      [-5, 1, ROOK],
      [5, 1, ROOK],
      [-4, 2, BISHOP],
      [4, 2, BISHOP],
      [-6, 0, KNIGHT],
      [6, 0, KNIGHT],
      [0, 0, PAWN],
      [0, 2, PAWN]
    ]
  },
  {
    id: "citadel",
    name: "The Citadel",
    weight: 7,
    minStage: 5,
    // The one camp that is a siege. Only put in front of an army that can
    // answer it: against a lone king it is not a hard fight, it is a wall with
    // a stage counter behind it, and the run simply stops.
    minPlayerPieces: 8,
    ground: [
      // An outer wall, hollow.
      ...block(-5, 5, 0, 0),
      ...block(-5, 5, 8, 8),
      ...block(-5, -5, 1, 7),
      ...block(5, 5, 1, 7),
      // The inner ward, and the two gates that reach it.
      ...block(-2, 2, 3, 5),
      [0, 1],
      [0, 2],
      [0, 6],
      [0, 7]
    ],
    pieces: [
      [0, 4, KING],
      [0, 3, QUEEN],
      [-5, 4, ROOK],
      [5, 4, ROOK],
      [-2, 5, BISHOP],
      [2, 5, BISHOP],
      [-2, 3, KNIGHT],
      [2, 3, KNIGHT],
      [-3, 0, PAWN],
      [-1, 0, PAWN],
      [1, 0, PAWN],
      [3, 0, PAWN]
    ]
  }
];

/**
 * Every camp a cleared stage may open onto — the ones written above, and the
 * ones **drawn**.
 *
 * A hand-written entry is a good way to say a shape and a poor way to say a
 * *place*: nobody lays out a ragged lobe with a silver seam under it and a
 * warehouse on its flank by typing coordinate pairs. So a room in a `.tmj`
 * carrying a `camp` object is converted at build time
 * (`tools/buildCamps.mjs`) into exactly this shape and concatenated here.
 *
 * **Concatenated rather than kept apart**, which is the whole point: an
 * authored camp is chosen, weighted, gated, anchored, oriented and validated by
 * the machinery that already exists. A second list would be a second set of
 * rules about how a camp arrives, and the first thing to go wrong with it
 * would be an authored camp that skips the check `formations.test.ts` makes of
 * every other one.
 */
export const FORMATIONS: readonly Formation[] = [...HAND_WRITTEN, ...AUTHORED_CAMPS];


/**
 * Rotate a relative coordinate so the camp faces the board it is attached to.
 *
 * `facing` is which way the camp is being appended: 0 north of the board, 1
 * east, 2 south, 3 west. The table is written as though approached from the
 * south, which is the case a reader can hold in their head.
 */
export function orient([file, rank]: Cell, facing: number): Cell {
  switch (facing & 3) {
    case 1:
      return [rank, -file];
    case 2:
      return [-file, -rank];
    case 3:
      return [-rank, file];
    default:
      return [file, rank];
  }
}

export interface PlacedCamp {
  cells: number[];
  pieces: [number, number][];
  /** The camp's industry, absolute and oriented. Absent when it has none. */
  heaps?: [number, number][];
  metal?: [number, number, number][];
  facing?: [number, number][];
  structures?: [number, number, Side][];
}

/**
 * A facing, turned the way the camp is turned.
 *
 * `orient` rotates a cell a quarter turn at a time, and a direction index has
 * to make the same turn or a camp drawn facing the player faces them from one
 * approach and away from the other three. Directions run north, east, south,
 * west — the same order and the same sense the cell rotation has — so the turn
 * is addition, which is the whole reason the index is worth keeping in that
 * order.
 */
export function orientFacing(direction: number, facing: number): number {
  return (direction + facing) & 3;
}

/** Put a formation on the board at `anchor`, facing `facing`, for `side`. */
export function placeFormation(
  formation: Formation,
  anchorFile: number,
  anchorRank: number,
  facing: number,
  side: Side
): PlacedCamp {
  const cells: number[] = [];
  const seen = new Set<number>();
  for (const cell of formation.ground) {
    const [file, rank] = orient(cell, facing);
    const sq = square(anchorFile + file, anchorRank + rank);
    if (seen.has(sq)) continue;
    seen.add(sq);
    cells.push(sq);
  }

  const pieces: [number, number][] = [];
  for (const [file, rank, type] of formation.pieces) {
    const [df, dr] = orient([file, rank], facing);
    const sq = square(anchorFile + df, anchorRank + dr);
    // Only ever onto ground this formation actually laid: a piece in a hole
    // cannot move, and a king in one cannot be mated.
    if (!seen.has(sq)) continue;
    pieces.push([sq, makePiece(side, type)]);
  }

  // The camp's industry, if it has any. Every one lands only on ground this
  // formation actually laid — the rule pieces follow, for the same reason: a
  // heap in a hole cannot be mined and a belt in one has nothing under it.
  const at = (file: number, rank: number): number | null => {
    const [df, dr] = orient([file, rank], facing);
    const sq = square(anchorFile + df, anchorRank + dr);
    return seen.has(sq) ? sq : null;
  };

  const camp: PlacedCamp = { cells, pieces };
  for (const [file, rank, amount] of formation.heaps ?? []) {
    const sq = at(file, rank);
    if (sq !== null) (camp.heaps ??= []).push([sq, amount]);
  }
  for (const [file, rank, grade, count] of formation.metal ?? []) {
    const sq = at(file, rank);
    if (sq !== null) (camp.metal ??= []).push([sq, grade, count]);
  }
  for (const [file, rank, direction] of formation.facing ?? []) {
    const sq = at(file, rank);
    if (sq !== null) (camp.facing ??= []).push([sq, orientFacing(direction, facing)]);
  }
  for (const [file, rank, kind] of formation.structures ?? []) {
    const sq = at(file, rank);
    // Garrisoned by whoever the camp belongs to, never by whoever was side 1
    // in the level it was drawn for.
    if (sq !== null) (camp.structures ??= []).push([sq, kind, side]);
  }

  return camp;
}

/**
 * A camp at roughly `weight` in difficulty.
 *
 * Drawn from the *strongest* camps the stage can afford rather than from every
 * camp at or below it. Uniform over all affordable meant a fourth-stage army
 * still had a one-in-four chance of being the five-piece outpost you cleared
 * on stage one, so the difficulty curve was a curve on average and a coin
 * flip in practice — and losing that coin flip is the encounter a player
 * remembers as "too easy".
 */
/** Is this camp one the board is ready for? */
export function admits(formation: Formation, muster: Muster): boolean {
  if (formation.weight > Math.max(1, muster.tier)) return false;
  if (formation.minStage !== undefined && muster.stage < formation.minStage) return false;
  if (
    formation.minPlayerPieces !== undefined &&
    muster.playerPieces < formation.minPlayerPieces
  ) {
    return false;
  }
  if (
    formation.maxPlayerPieces !== undefined &&
    muster.playerPieces > formation.maxPlayerPieces
  ) {
    return false;
  }
  return true;
}

export function formationFor(muster: Muster, roll: number): Formation {
  const affordable = FORMATIONS.filter((entry) => admits(entry, muster));
  // Never empty-handed. A stage with nothing to put on it is a stage that
  // cannot turn over, and the smallest camp is always a legal answer — the
  // conditions are there to make the *choice* better, not to be able to
  // refuse to make one.
  if (affordable.length === 0) return FORMATIONS[0];

  // Within one tier of the best on offer: enough spread that consecutive
  // stages are not identical, not enough to hand back the opening camp.
  const best = Math.max(...affordable.map((entry) => entry.weight));
  const pool = affordable.filter((entry) => entry.weight >= best - 1);
  return pool[Math.min(pool.length - 1, Math.floor(roll * pool.length))];
}

/**
 * Extra rank for a camp on a stage past the end of the table.
 *
 * The table stops at four and the stages do not, so beyond it the same camps
 * come back with better pieces in them: the front pawns become knights, then
 * bishops, then rooks. Deterministic in the stage, and it only ever upgrades
 * pawns, so a camp keeps the shape that its ground was drawn to fit.
 */
export function reinforce(
  pieces: readonly Placement[],
  over: number
): readonly Placement[] {
  if (over <= 0) return pieces;
  const ladder: PieceType[] = [KNIGHT, BISHOP, ROOK, QUEEN];
  const upgrade = ladder[Math.min(ladder.length - 1, over - 1)];
  // The last `over` pawns in table order, so the count grows with the stage
  // and the choice never depends on iteration order.
  let remaining = over;
  const out: Placement[] = [];
  for (let i = pieces.length - 1; i >= 0; i--) {
    const [file, rank, type] = pieces[i];
    if (type === PAWN && remaining > 0) {
      remaining--;
      out.unshift([file, rank, upgrade]);
    } else {
      out.unshift(pieces[i]);
    }
  }
  return out;
}
